import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Select,
  SelectItem,
} from "@heroui/react";
import { useDispatch } from "react-redux";
import { editParagraphAsync } from "../../../redux/paragraph";
import { getFullTranscriptionByIdAsync } from "../../../redux/transcription";
import { pushUndo, buildUndoEntry, fetchSnapshots } from "../../../redux/undo";
import AppButton from "../../common/AppButton";


// Format seconds → hh:mm:ss
function formatTime(seconds) {
  const h = Math.floor(seconds / 3600)
    .toString()
    .padStart(2, "0");
  const m = Math.floor((seconds % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${h}:${m}:${s}`;
}

// Parse hh:mm:ss → seconds
function parseTime(hhmmss) {
  const parts = hhmmss.split(":").map(parseFloat).reverse();
  const [s = 0, m = 0, h = 0] = parts;
  return h * 3600 + m * 60 + s;
}

// Convert seconds → ISO 8601 (e.g. PT4M2.64S)
function secondsToISO8601Duration(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = (totalSeconds % 60).toFixed(2).replace(/\.?0+$/, ""); // trim .00 if needed

  let result = "PT";
  if (hours > 0) result += `${hours}H`;
  if (minutes > 0 || hours > 0) result += `${minutes}M`;
  if (seconds > 0 || (hours === 0 && minutes === 0)) result += `${seconds}S`;

  return result;
}


export default function EditParagraphMetaModal({
  paragraph,
  isOpen,
  onClose,
  sessionId,
  speakers = [],
  onOpenSpeakersModal,
}) {
  const dispatch = useDispatch();

  const [speakerName, setSpeakerName] = useState(paragraph.speaker_name || "");
  const [selectedSpeakerKey, setSelectedSpeakerKey] = useState(paragraph.speaker_name ? new Set([paragraph.speaker_name]) : new Set());
  const [startHMS, setStartHMS] = useState(formatTime(paragraph.start || 0));

  const originalDuration = paragraph.end - paragraph.start;

  useEffect(() => {
    if (paragraph) {
      setSpeakerName(paragraph.speaker_name || "");
      setSelectedSpeakerKey(paragraph.speaker_name ? new Set([paragraph.speaker_name]) : new Set());
      setStartHMS(formatTime(paragraph.start || 0));
    }
  }, [paragraph]);

  const handleSave = async () => {
    const newStartSeconds = parseTime(startHMS);
    const newEndSeconds = newStartSeconds + originalDuration;

    const isoStart = secondsToISO8601Duration(newStartSeconds);

    // determine selected speaker and number
    const selectedKey = Array.from(selectedSpeakerKey || [])[0] || speakerName;
    const match = (Array.isArray(speakers) ? speakers : []).find((s) => s.key === selectedKey || s.label === selectedKey);
    const payload = {
      start: isoStart,
      speaker_name: selectedKey || speakerName,
    };
    if (match && (match.value !== undefined && match.value !== null)) {
      const num = Number(match.value);
      if (Number.isFinite(num)) payload.speaker_number = num;
    }

    // Canonical row before the change — the view holds `start` as a number
    // and writing that back would destroy the ISO timestamp.
    const [undoBefore] = await fetchSnapshots([paragraph.id]);

    await dispatch(
      editParagraphAsync({
        id: paragraph.id,
        payload
      })
    );

    if (undoBefore) {
      dispatch(pushUndo(buildUndoEntry({
        type: "speaker-time",
        label: "Sprecher & Startzeit",
        sessionId,
        before: [undoBefore],
        after: [{ ...undoBefore, ...payload }],
      })));
    }

    await dispatch(getFullTranscriptionByIdAsync(sessionId))

    onClose();
  };

  return (
    <Modal isOpen={isOpen} onOpenChange={onClose} size="2xl" placement="center">
      <ModalContent>
        {(modalClose) => (
          <>
            <ModalHeader>Sprecher & Startzeit ändern</ModalHeader>
            <ModalBody className="space-y-4">
              <Select
                label="Speaker"
                placeholder="Select a speaker"
                selectedKeys={selectedSpeakerKey}
                onSelectionChange={setSelectedSpeakerKey}
                className="w-full"
              >
                {(Array.isArray(speakers) ? speakers : []).map((sp) => (
                  <SelectItem key={sp.key}>
                    {sp.label || sp.key}
                  </SelectItem>
                ))}
              </Select>
              <Input
                label="Start Time (hh:mm:ss)"
                placeholder="00:04:02"
                value={startHMS}
                onChange={(e) => setStartHMS(e.target.value)}
              />
            </ModalBody>
            <ModalFooter className="justify-between">
              <div>
                {onOpenSpeakersModal && (
                  <AppButton width="w-[190px]" onClick={onOpenSpeakersModal}>
                    Sprecher verwalten
                  </AppButton>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="bordered" color="danger" radius="full" onPress={onClose}>
                  abbrechen
                </Button>
                <AppButton width="w-[190px]" onClick={handleSave}>
                  Änderungen speichern
                </AppButton>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
